import {
  IBidSettingsValue,
  IClientSettings,
  ICrewEventLedger,
  ICrewGroupReleaseSchedule,
  ICrewLogicModifier,
  ICrewsNonFlyingAssignment,
  IDisplayPairing,
  ILogicModifier,
  INonFlyingAssignment,
  IOCAPIError,
  IPairing,
  IQualificationCheckResult,
  IUser,
  IViolationCache,
  IViolationCheckStatus,
} from "@vmosolutions/oc-interfaces";
import { API, Auth } from "aws-amplify";
import moment from "moment-timezone";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { IHoveredEvent } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { checkIntervalBlockIsLoaded } from "src/pages/FlightTimeline/FlightTimeline";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  CrewSchedEvent,
  IAnchoredHoveredEvents,
  IAnchoredHoveredViolation,
  ICrewLogicModifierMap,
  ICrewMember,
  ICrewNonFlyingAssignmentMap,
  ICrewPairingMap,
  ICrewSchedEventMap,
  ICrewTimeOption,
  ICrewViolations,
  IFocusCrewSchedArea,
  ISelectedCrewDay,
  ISelectedCrewDays,
  isIDisplayPairing,
  ITemporaryPairing,
  ITimeIntervalLoaded,
} from "../../reducers/CrewScheduleReducer";
import { IAirportOption } from "../../reducers/StructuresReducer";
import { getNonFlyingAssignments } from "./nonFlyingAssignments";
import { getPairings } from "./pairings";

// #region Action Types Enumerator

export enum Type {
  SET_WINDOW_TIMEZONE = "SET_WINDOW_TIMEZONE",
  UPDATE_CREWMEMBER = "UPDATE_CREWMEMBER",
  GOT_CREW = "GOT_CREW",
  FILTERED_CREW = "FILTERED_CREW",
  REMOVE_FILTERED_CREW = "REMOVE_FILTERED_CREW",
  GOT_PAIRINGS = "GOT_PAIRINGS",
  TOGGLE_SELECTED_CREW_MEMBER = "TOGGLE_SELECTED_CREW_MEMBER",
  TOGGLE_EXPANDED_CREW_ROW = "TOGGLE_EXPANDED_CREW_ROW",
  EXPAND_COLLAPSE_ALL_CREW_ROWS = "EXPAND_COLLAPSE_ALL_CREW_ROWS",
  SET_SCROLL_TOP = "SET_SCROLL_TOP",
  GOT_NONFLYING_ASSIGNMENTS = "GOT_NONFLYING_ASSIGNMENTS",
  SET_SELECTED_CREW_EVENTS = "SET_SELECTED_CREW_EVENTS",
  GOT_VIOLATIONS = "GOT_VIOLATIONS",
  CLEAR_CREW_VIOLATIONS = "CLEAR_CREW_VIOLATIONS",
  CLEAR_ALL_VIOLATIONS = "CLEAR_ALL_VIOLATIONS",
  SET_CLEAR_VIOLATIONS_TIMEOUT_ID = "SET_CLEAR_VIOLATIONS_TIMEOUT_ID",
  IS_LOADING_CREW_VIOLATIONS = "IS_LOADING_CREW_VIOLATIONS",
  LOADED_INITIAL_CREW_VIOLATIONS = "LOADED_INITIAL_CREW_VIOLATIONS",
  LOADED_DATA_INTERVAL = "LOADED_DATA_INTERVAL",
  TIMELINE_CREW_DATA_LOADING = "TIMELINE_CREW_DATA_LOADING",
  IS_LOADING = "IS_LOADING",
  ADD_TEMPORARY_PAIRING = "ADD_TEMPORARY_PAIRING",
  UPDATE_TEMPORARY_PAIRING = "UPDATE_TEMPORARY_PAIRING",
  REMOVE_TEMPORARY_PAIRING = "REMOVE_TEMPORARY_PAIRING",
  SET_SELECTED_CREW_DAY = "SET_SELECTED_CREW_DAY",
  SET_SELECTED_CREW_DAYS = "SET_SELECTED_CREW_DAYS",
  SET_SELECTED_CREW_DAY_MEMBER = "SET_SELECTED_CREW_DAY_MEMBER",
  SET_SELECTED_CREW_DAY_TIME = "SET_SELECTED_CREW_DAY_TIME",
  UPDATE_CREW_PAIRINGS = "UPDATE_CREW_PAIRINGS",
  UPDATE_CREW_NFAS = "UPDATE_CREW_NFAS",
  GOT_PLANNED_EVENTS = "GOT_PLANNED_EVENTS",
  UPDATE_PLANNED_EVENTS = "UPDATE_PLANNED_EVENTS",
  SET_CREW_BASE = "SET_CREW_BASE",
  SET_HOVERED_EVENTS = "SET_HOVERED_EVENTS",
  SET_FOCUSED_AREA = "SET_FOCUSED_AREA",
  SET_FOCUSED_PAIRING = "SET_FOCUSED_PAIRING",
  SET_DISPLAY_TIMEZONE = "SET_DISPLAY_TIMEZONE",
  SET_CREW_GROUP_FILTER = "SET_CREW_GROUP_FILTER",
  GOT_DAY_LOGIC_MODIFIERS = "GOT_DAY_LOGIC_MODIFIERS",
  SET_DAY_LOGIC_MODIFIERS = "SET_DAY_LOGIC_MODIFIERS",
  UPDATE_SEARCH_CREW_MEMBER = "UPDATE_SEARCH_CREW_MEMBER",
  UPDATE_CREW_QUALIFICATIONS = "UPDATE_CREW_QUALIFICATIONS",
  IS_LOADING_CREW_QUALIFICATIONS = "IS_LOADING_CREW_QUALIFICATIONS",
  SET_HOVERED_VIOLATION = "SET_HOVERED_VIOLATION",
  UPDATE_CREW_VIOLATIONS = "UPDATE_CREW_VIOLATIONS",
  SET_HIDE_DROPOFF_HOURS = "SET_HIDE_DROPOFF_HOURS",
  GOT_CREW_MEMBER_LEDGER = "GOT_CREW_MEMBER_LEDGER",
  ADD_PAIRING_EDITOR = "ADD_PAIRING_EDITOR",
  REMOVE_PAIRING_EDITOR = "REMOVE_PAIRING_EDITOR",
  UPDATE_PAIRING_EDITOR = "UPDATE_PAIRING_EDITOR",
  REMOVE_WINDOW_PROPERTIES = "REMOVE_WINDOW_PROPERTIES",
  UPDATE_WATERFALL_PAIRINGS = "UPDATE_WATERFALL_PAIRINGS",
  SET_WATERFALL_WINDOW_LOCK = "SET_WATERFALL_WINDOW_LOCK",
  GOT_VIOLATION_CHECK_STATUS = "GOT_VIOLATION_CHECK_STATUS",
  SET_HIDE_TIME_BETWEEN_EVENTS = "SET_HIDE_TIME_BETWEEN_EVENTS",
  SET_HIDE_CHECKIN_CHECKOUT_TIMES = "SET_HIDE_CHECKIN_CHECKOUT_TIMES",
  DESELECT_COLLAPSE_ALL_ROWS = "DESELECT_COLLAPSE_ALL_ROWS",
  TIMELINE_LINK = "TIMELINE_LINK",
  SET_ACTIVE_OPEN_FLYING_REQUEST = "SET_ACTIVE_OPEN_FLYING_REQUEST",
  SET_LAST_OPEN_FLYING_ASSIGNMENT_RESPONSE = "SET_LAST_OPEN_FLYING_ASSIGNMENT_RESPONSE",
  SET_BIDDING_SETTINGS = "SET_BIDDING_SETTINGS",
}

// #endregion

// #region Action Interfaces

export interface ISetWindowTimezone {
  type: Type.SET_WINDOW_TIMEZONE;
  windowId: string;
  displayTimezone: IAirportOption;
}

export interface ISetHideDropOffHours {
  type: Type.SET_HIDE_DROPOFF_HOURS;
  hideDropOffHours: boolean;
}

export interface ISetWindowHideCheckInCheckOutTimes {
  type: Type.SET_HIDE_CHECKIN_CHECKOUT_TIMES;
  windowId: string;
  hideCheckInCheckOutTimes: boolean;
}

export interface ISetWindowHideTimeBetweenEvents {
  type: Type.SET_HIDE_TIME_BETWEEN_EVENTS;
  windowId: string;
  hideTimeBetweenEvents: boolean;
}

export interface IUpdateCrewMber {
  type: Type.UPDATE_CREWMEMBER;
  mode: string;
  crew: IUser;
}
export interface IGotCrew {
  type: Type.GOT_CREW;
  crew: ICrewMember[];
}

export interface IFilteredCrew {
  type: Type.FILTERED_CREW | Type.REMOVE_FILTERED_CREW;
  filtered: ICrewMember[];
  windowId: string;
}

export interface IGotPairings {
  type: Type.GOT_PAIRINGS;
  pairings: IDisplayPairing[];
}

export interface IGotNonFlyingAssignments {
  type: Type.GOT_NONFLYING_ASSIGNMENTS;
  nonFlyingAssignments: ICrewsNonFlyingAssignment[];
}

export interface IToggleSelectedCrewMember {
  type: Type.TOGGLE_SELECTED_CREW_MEMBER;
  id: string;
  forceTrue?: boolean;
  forceExpand?: boolean;
}

export interface IToggleExpandedCrewRow {
  type: Type.TOGGLE_EXPANDED_CREW_ROW;
  id: string;
}

export interface IExpandCollapseAllCrewRows {
  type: Type.EXPAND_COLLAPSE_ALL_CREW_ROWS;
  state: boolean;
}

export interface ISetScrollTop {
  type: Type.SET_SCROLL_TOP;
  scrollAmount: number;
}

export interface ISetSelectedCrewEvents {
  type: Type.SET_SELECTED_CREW_EVENTS;
  newEvents: IHoveredEvent[];
  shiftKey: boolean;
  ctrlKey: boolean;
  shouldFocus?: boolean;
  /** Only true if the selected events have been updated ie pairing update websocket etc */
  update?: boolean;
}

export interface IGotViolations {
  type: Type.GOT_VIOLATIONS;
  crewMap: ICrewViolations;
  crewId: string;
  createdAt?: number;
}

export interface IClearCrewViolations {
  type: Type.CLEAR_CREW_VIOLATIONS;
  crewId: string;
}

export interface IClearAllViolations {
  type: Type.CLEAR_ALL_VIOLATIONS;
}

export interface ISetClearViolationsTimeoutId {
  type: Type.SET_CLEAR_VIOLATIONS_TIMEOUT_ID;
  timeoutId: NodeJS.Timeout; //this is just type number not the timer function
}

export interface IIsLoadingCrewViolations {
  type: Type.IS_LOADING_CREW_VIOLATIONS;
  isLoading: boolean;
}

export interface ILoadedCrewViolations {
  type: Type.LOADED_INITIAL_CREW_VIOLATIONS;
  loaded: boolean;
}
export interface ISetLoadedDataInterval {
  type: Type.LOADED_DATA_INTERVAL;
  interval: ITimeIntervalLoaded;
}

export interface ICrewDataLoading {
  type: Type.TIMELINE_CREW_DATA_LOADING;
  interval: string;
  loaded: boolean;
}
export interface IIsLoading {
  type: Type.IS_LOADING;
  loading: boolean;
}

export interface IAddTemporaryPairing {
  type: Type.ADD_TEMPORARY_PAIRING;
  temporaryPairing: ITemporaryPairing;
}

export interface IUpdateTemporaryPairing {
  type: Type.UPDATE_TEMPORARY_PAIRING;
  temporaryPairing: ITemporaryPairing;
}
export interface IRemoveTemporaryPairing {
  type: Type.REMOVE_TEMPORARY_PAIRING;
  pairingId: string;
  crewId?: string;
}

export interface ISetSelectedCrewDay {
  type: Type.SET_SELECTED_CREW_DAY;
  selectedCrewDay: ISelectedCrewDay;
}

export interface ISetSelectedCrewDays {
  type: Type.SET_SELECTED_CREW_DAYS;
  selectedCrewDays: ISelectedCrewDays | null;
}

export interface ISetSelectedCrewDayMember {
  type: Type.SET_SELECTED_CREW_DAY_MEMBER;
  newCrewId: string;
}

export interface ISetSelectedCrewDayTime {
  type: Type.SET_SELECTED_CREW_DAY_TIME;
  selectedTime: number;
}

export interface IUpdateCrewPairings {
  type: Type.UPDATE_CREW_PAIRINGS;
  crewPairingMap: ICrewPairingMap;
}

export interface IUpdateCrewNFAs {
  type: Type.UPDATE_CREW_NFAS;
  crewNFAMap: ICrewNonFlyingAssignmentMap;
}

export interface IGotPlannedEvents {
  type: Type.GOT_PLANNED_EVENTS;
  plannedEvents: CrewSchedEvent[];
}

export interface IUpdatedPlannedEvents {
  type: Type.UPDATE_PLANNED_EVENTS;
  crewPlannedEvents: ICrewSchedEventMap;
}

export interface ISetCrewBase {
  type: Type.SET_CREW_BASE;
  crewBase: ICrewTimeOption;
  windowId: string;
}

export interface ISetHoveredEvents {
  type: Type.SET_HOVERED_EVENTS;
  hoveredEvents: IAnchoredHoveredEvents;
}

export interface ISetFocusedArea {
  type: Type.SET_FOCUSED_AREA;
  focusedArea: IFocusCrewSchedArea;
  windowId: string;
}

export interface ISetFocusedPairing {
  type: Type.SET_FOCUSED_PAIRING;
  focusedPairing: string;
}

export interface ISetCrewGroupFilter {
  type: Type.SET_CREW_GROUP_FILTER;
  groups: string[];
  windowId: string;
}

export interface IGotDayLogicModifiers {
  type: Type.GOT_DAY_LOGIC_MODIFIERS;
  dayLogicModifiers: ICrewLogicModifier[];
}

export interface ISetDayLogicModifiers {
  type: Type.SET_DAY_LOGIC_MODIFIERS;
  crewDayLogicModifiers: ICrewLogicModifierMap;
}

export interface IUpdateSearchCrewMember {
  type: Type.UPDATE_SEARCH_CREW_MEMBER;
  windowId: string;
  searchedCrewMembers: string[];
}

export interface IUpdateCrewQualifications {
  type: Type.UPDATE_CREW_QUALIFICATIONS;
  qualifications: IQualificationCheckResult[];
  crewIds?: string[];
}

export interface IIsLoadingCrewQualifications {
  type: Type.IS_LOADING_CREW_QUALIFICATIONS;
  isLoading: boolean;
}

export interface ISetHoveredViolation {
  type: Type.SET_HOVERED_VIOLATION;
  hoveredViolation: IAnchoredHoveredViolation;
}

export interface IUpdateCrewViolations {
  type: Type.UPDATE_CREW_VIOLATIONS;
  violations: IViolationCache[];
}

export interface IGotCrewMemberLedger {
  type: Type.GOT_CREW_MEMBER_LEDGER;
  crewLedgers: ICrewEventLedger[];
  crewId: string;
}
export interface IAddPairingEditor {
  type: Type.ADD_PAIRING_EDITOR;
  pairing: IPairing;
}

export interface IRemovePairingEditor {
  type: Type.REMOVE_PAIRING_EDITOR;
  pairingId: string;
}

export interface IUpdatePairingEditor {
  type: Type.UPDATE_PAIRING_EDITOR;
  pairingId: string;
  updates: Partial<IPairing>;
}

export interface IWindowProperties {
  type: Type.REMOVE_WINDOW_PROPERTIES;
  windowId: string;
}

export interface IUpdateWaterfallPairings {
  type: Type.UPDATE_WATERFALL_PAIRINGS;
  pairings: IDisplayPairing[];
}
export interface IViolCheckStatus {
  type: Type.GOT_VIOLATION_CHECK_STATUS;
  crewToUpdate: { [crewId: string]: IViolationCheckStatus };
}

export interface IDeselectCollapseAll {
  type: Type.DESELECT_COLLAPSE_ALL_ROWS;
}

export interface ISetTimelineLink {
  type: Type.TIMELINE_LINK;
  start: number;
  end: number;
  activeWindowId: string;
}

export interface ISetActiveOpenFlyingPairing {
  type: Type.SET_ACTIVE_OPEN_FLYING_REQUEST;
  windowId: string;
  requestIDs: any[];
  pairingID: string | null;
}

export interface ISetLastOpenFlyingAssignmentResponse {
  type: Type.SET_LAST_OPEN_FLYING_ASSIGNMENT_RESPONSE;
  assignedAt: Date;
  requestResponse: any;
}

export interface ISetBiddingSettings {
  type: Type.SET_BIDDING_SETTINGS;
  biddingSettings: IBidSettingsValue;
}

// #endregion

// #region Action Union Type

export type Action =
  | ISetWindowTimezone
  | IUpdateCrewMber
  | IGotCrew
  | IFilteredCrew
  | IGotPairings
  | IToggleSelectedCrewMember
  | IToggleExpandedCrewRow
  | IExpandCollapseAllCrewRows
  | ISetScrollTop
  | IGotNonFlyingAssignments
  | ISetSelectedCrewEvents
  | IGotViolations
  | IClearAllViolations
  | ISetClearViolationsTimeoutId
  | ISetLoadedDataInterval
  | ICrewDataLoading
  | IIsLoading
  | IAddTemporaryPairing
  | IUpdateTemporaryPairing
  | IRemoveTemporaryPairing
  | ISetSelectedCrewDay
  | ISetSelectedCrewDays
  | ISetSelectedCrewDayMember
  | ISetSelectedCrewDayTime
  | IUpdateCrewPairings
  | IUpdateCrewNFAs
  | IGotPlannedEvents
  | IUpdatedPlannedEvents
  | ISetCrewBase
  | ISetHoveredEvents
  | ISetFocusedArea
  | ISetFocusedPairing
  | ISetCrewGroupFilter
  | IGotDayLogicModifiers
  | ISetDayLogicModifiers
  | IUpdateSearchCrewMember
  | IUpdateCrewQualifications
  | ISetHoveredViolation
  | IUpdateCrewViolations
  | ISetHideDropOffHours
  | IGotCrewMemberLedger
  | IClearCrewViolations
  | ILoadedCrewViolations
  | IIsLoadingCrewViolations
  | IIsLoadingCrewQualifications
  | IAddPairingEditor
  | IRemovePairingEditor
  | IWindowProperties
  | IUpdateWaterfallPairings
  | IViolCheckStatus
  | ISetWindowHideTimeBetweenEvents
  | ISetWindowHideCheckInCheckOutTimes
  | IDeselectCollapseAll
  | ISetTimelineLink
  | ISetActiveOpenFlyingPairing
  | ISetLastOpenFlyingAssignmentResponse
  | ISetBiddingSettings
  | IUpdatePairingEditor;

export const setBiddingSettings = (biddingSettings: IBidSettingsValue): ISetBiddingSettings => {
  return {
    type: Type.SET_BIDDING_SETTINGS,
    biddingSettings,
  };
};

export const setTimelineLink = (start?: number, end?: number, activeWindowId?: string): ISetTimelineLink => {
  return {
    type: Type.TIMELINE_LINK,
    start,
    end,
    activeWindowId,
  };
};

export const updateWaterfallPairings = (pairings: IDisplayPairing[]): IUpdateWaterfallPairings => {
  return { type: Type.UPDATE_WATERFALL_PAIRINGS, pairings };
};

// #region Action Creators (Synchronous)
export const gotCrewLedgers = (crewLedgers: ICrewEventLedger[], crewId: string): IGotCrewMemberLedger => {
  return { type: Type.GOT_CREW_MEMBER_LEDGER, crewLedgers, crewId };
};

export const setWindowTimezone = (windowId: string, displayTimezone: IAirportOption): ISetWindowTimezone => {
  return {
    type: Type.SET_WINDOW_TIMEZONE,
    windowId,
    displayTimezone,
  };
};

export const updateCrewMember = (crew: IUser, mode: string): IUpdateCrewMber => {
  return {
    type: Type.UPDATE_CREWMEMBER,
    mode,
    crew,
  };
};

export const gotCrewAction = (crew: ICrewMember[]): IGotCrew => {
  return {
    type: Type.GOT_CREW,
    crew,
  };
};

export const setFilteredCrew = (filtered: ICrewMember[], windowId: string): IFilteredCrew => {
  return {
    type: Type.FILTERED_CREW,
    filtered,
    windowId,
  };
};

export const removeFilteredCrew = (filtered: ICrewMember[], windowId: string): IFilteredCrew => {
  return {
    type: Type.REMOVE_FILTERED_CREW,
    filtered,
    windowId,
  };
};

export const setCrewBase = (crewBase: ICrewTimeOption, windowId: string): ISetCrewBase => {
  return {
    type: Type.SET_CREW_BASE,
    crewBase,
    windowId,
  };
};

export const setCrewGroupFilter = (groups: string[], windowId: string): ISetCrewGroupFilter => {
  return {
    type: Type.SET_CREW_GROUP_FILTER,
    groups,
    windowId,
  };
};

export const gotPairings = (pairings: IDisplayPairing[]): IGotPairings => {
  return {
    type: Type.GOT_PAIRINGS,
    pairings,
  };
};

export const gotViolations = (crewMap: ICrewViolations, crewId: string, createdAt?: number): IGotViolations => {
  return {
    type: Type.GOT_VIOLATIONS,
    crewMap,
    crewId,
    createdAt,
  };
};

export const clearCrewViolations = (crewId: string): IClearCrewViolations => {
  return {
    type: Type.CLEAR_CREW_VIOLATIONS,
    crewId: crewId,
  };
};

export const clearAllViolations = (): IClearAllViolations => {
  return {
    type: Type.CLEAR_ALL_VIOLATIONS,
  };
};

export const setClearViolationsTimeoutId = (timer: NodeJS.Timeout): ISetClearViolationsTimeoutId => {
  return {
    type: Type.SET_CLEAR_VIOLATIONS_TIMEOUT_ID,
    timeoutId: timer,
  };
};

export const isLoadingCrewViolations = (isLoading: boolean): IIsLoadingCrewViolations => {
  return {
    type: Type.IS_LOADING_CREW_VIOLATIONS,
    isLoading: isLoading,
  };
};

export const loadedCrewViolations = (loaded: boolean): ILoadedCrewViolations => {
  return {
    type: Type.LOADED_INITIAL_CREW_VIOLATIONS,
    loaded: loaded,
  };
};

export const gotViolationCheckStatus = (crewToUpdate: {
  [crewId: string]: IViolationCheckStatus;
}): IViolCheckStatus => {
  return {
    type: Type.GOT_VIOLATION_CHECK_STATUS,
    crewToUpdate,
  };
};

export const gotNonFlyingAssignments = (
  nonFlyingAssignments: ICrewsNonFlyingAssignment[]
): IGotNonFlyingAssignments => {
  return {
    type: Type.GOT_NONFLYING_ASSIGNMENTS,
    nonFlyingAssignments,
  };
};

export const gotDayLogicModifiers = (dayLogicModifiers: ICrewLogicModifier[]): IGotDayLogicModifiers => {
  return {
    type: Type.GOT_DAY_LOGIC_MODIFIERS,
    dayLogicModifiers,
  };
};

// Special function triggered when switching from normal to bid assignment mode.
export const deselectCollapseAllRows = (): IDeselectCollapseAll => {
  return { type: Type.DESELECT_COLLAPSE_ALL_ROWS };
};

export const toggleSelectedCrewMemberAction = (
  id: string,
  forceTrue?: boolean,
  forceExpand?: boolean
): IToggleSelectedCrewMember => {
  return {
    type: Type.TOGGLE_SELECTED_CREW_MEMBER,
    id,
    forceTrue,
    forceExpand,
  };
};

export const toggleExpandedCrewRowAction = (id: string): IToggleExpandedCrewRow => {
  return {
    type: Type.TOGGLE_EXPANDED_CREW_ROW,
    id,
  };
};

export const expandCollapseAllCrewRows = (state: boolean): IExpandCollapseAllCrewRows => {
  return {
    type: Type.EXPAND_COLLAPSE_ALL_CREW_ROWS,
    state,
  };
};

export const setScrollTopAction = (scrollAmount: number): ISetScrollTop => {
  return {
    type: Type.SET_SCROLL_TOP,
    scrollAmount,
  };
};

export const setSelectedCrewEvents = (
  newEvents: IHoveredEvent[],
  shiftKey: boolean,
  ctrlKey: boolean,
  shouldFocus: boolean
): ISetSelectedCrewEvents => {
  return {
    type: Type.SET_SELECTED_CREW_EVENTS,
    newEvents,
    shiftKey,
    ctrlKey,
    shouldFocus,
  };
};

export const setSelectedCrewDay = (selectedCrewDay: ISelectedCrewDay): ISetSelectedCrewDay => {
  return {
    type: Type.SET_SELECTED_CREW_DAY,
    selectedCrewDay,
  };
};

export const setSelectedCrewDays = (selectedCrewDays: ISelectedCrewDays | null): ISetSelectedCrewDays => {
  return {
    type: Type.SET_SELECTED_CREW_DAYS,
    selectedCrewDays,
  };
};

export const setSelectedCrewDayMember = (newCrewId: string): ISetSelectedCrewDayMember => {
  return {
    type: Type.SET_SELECTED_CREW_DAY_MEMBER,
    newCrewId,
  };
};

export const setSelectedCrewDayTime = (selectedTime: number): ISetSelectedCrewDayTime => {
  return {
    type: Type.SET_SELECTED_CREW_DAY_TIME,
    selectedTime,
  };
};

export const setFocusedCrewSchedArea = (focusedArea: IFocusCrewSchedArea, windowId?: string): ISetFocusedArea => {
  return {
    type: Type.SET_FOCUSED_AREA,
    focusedArea,
    windowId,
  };
};

export const setFocusedCrewSchedPairing = (focusedPairing: string): ISetFocusedPairing => {
  return {
    type: Type.SET_FOCUSED_PAIRING,
    focusedPairing,
  };
};

export const setLoadedDataInterval = (interval: ITimeIntervalLoaded): ISetLoadedDataInterval => {
  return {
    type: Type.LOADED_DATA_INTERVAL,
    interval,
  };
};

export const crewDataLoading = (loading: boolean): IIsLoading => {
  return {
    type: Type.IS_LOADING,
    loading,
  };
};

export const addTemporaryPairing = (temporaryPairing: ITemporaryPairing): IAddTemporaryPairing => {
  return {
    type: Type.ADD_TEMPORARY_PAIRING,
    temporaryPairing,
  };
};

export const updateTemporaryPairing = (temporaryPairing: ITemporaryPairing): IUpdateTemporaryPairing => {
  return {
    type: Type.UPDATE_TEMPORARY_PAIRING,
    temporaryPairing,
  };
};

export const removeCrewTemporaryPairing = (pairingId: any, crewId: any): IRemoveTemporaryPairing => {
  return {
    type: Type.REMOVE_TEMPORARY_PAIRING,
    pairingId: pairingId,
    crewId: crewId,
  };
};

export const removeTemporaryPairing = (pairingId: any): IRemoveTemporaryPairing => {
  return {
    type: Type.REMOVE_TEMPORARY_PAIRING,
    pairingId: pairingId,
  };
};

export const gotPlannedEvents = (plannedEvents: CrewSchedEvent[]): IGotPlannedEvents => {
  return {
    type: Type.GOT_PLANNED_EVENTS,
    plannedEvents,
  };
};

export const setHoveredEvents = (hoveredEvents: IAnchoredHoveredEvents): ISetHoveredEvents => {
  return {
    type: Type.SET_HOVERED_EVENTS,
    hoveredEvents,
  };
};

/**
 * updateCrewQualifications - Updates the crewSchedule reducer to contain new qualification errors
 * @param qualifications - Array of qualification results to update/render in crew schedule timeline
 */
export const updateCrewQualifications = (
  qualifications: IQualificationCheckResult[],
  crewIds?: string[]
): IUpdateCrewQualifications => {
  return {
    type: Type.UPDATE_CREW_QUALIFICATIONS,
    qualifications,
    crewIds,
  };
};

export const isLoadingCrewQualifications = (isLoading: boolean): IIsLoadingCrewQualifications => {
  return {
    type: Type.IS_LOADING_CREW_QUALIFICATIONS,
    isLoading,
  };
};

/**
 * updateCrewViolations - Updates the crewSchedule reducer to contain new violation errors
 * @param violations - Array of violation results to update/render in crew schedule timeline
 */
export const updateCrewViolations = (violations: IViolationCache[]): IUpdateCrewViolations => {
  return {
    type: Type.UPDATE_CREW_VIOLATIONS,
    violations,
  };
};

export const setHoveredViolation = (hoveredViolation: IAnchoredHoveredViolation): ISetHoveredViolation => {
  return { type: Type.SET_HOVERED_VIOLATION, hoveredViolation };
};
// #endregion

// #region Async Actions
// basically API calls, not for dispatching (as far as I know)

export const getCrewMemberPlannedEvents = async (startTime: number, endTime: number): Promise<CrewSchedEvent[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const plannedPairings = await API.get("skytrains", `/pairings/planned?start=${startTime}&end=${endTime}`, {
      headers: { Accept: "*/*", Authorization: token },
    });

    const plannedNFAs = await API.get("skytrains", `/nfas/planned?start=${startTime}&end=${endTime}`, {
      headers: { Accept: "*/*", Authorization: token },
    });

    return [...plannedPairings, ...plannedNFAs];
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const fetchPairing = async (
  dispatch: ThunkDispatch<object, object, AnyAction>,
  startTime: number,
  endTime: number
): Promise<void> => {
  try {
    //get pairings
    const pairings = await getPairings(startTime, endTime);
    dispatch(gotPairings(pairings));
  } catch (e) {
    console.error(e);
  }
};

export const fetchPlannedEvents = async (
  dispatch: ThunkDispatch<object, object, AnyAction>,
  startTime: number,
  endTime: number
): Promise<void> => {
  try {
    //get planned events
    const plannedEvents = await getCrewMemberPlannedEvents(startTime, endTime);
    dispatch(gotPlannedEvents(plannedEvents));
  } catch (e) {
    console.error(e);
  }
};

export const fetchNFAs = async (
  dispatch: ThunkDispatch<object, object, AnyAction>,
  startTime: number,
  endTime: number
): Promise<void> => {
  try {
    //get NFAs
    const nonFlyingAssignments = await getNonFlyingAssignments(startTime, endTime);
    dispatch(gotNonFlyingAssignments(nonFlyingAssignments));
  } catch (e) {
    console.error(e);
  }
};

export const fetchDayTags = async (
  dispatch: ThunkDispatch<object, object, AnyAction>,
  startTime: number,
  endTime: number
): Promise<void> => {
  try {
    //get day tags
    const dayLogicModifier = await getDayLogicModifiers(startTime, endTime);
    dispatch(gotDayLogicModifiers(dayLogicModifier));
  } catch (e) {
    console.error(e);
  }
};

/**
 * If you want to load crew data in a certain range - this checks if the data is loaded, if it isn't than it will load it
 * @param startTime - the start of the interval to load
 * @param endTime - the end of the interval to load
 * @param numDaysForChunk - the number of days you want to load in an API call - default 31
 * @returns
 */
export const getCrewScheduleData = (
  startTime: number,
  endTime: number,
  numDaysForChunk = 31
): ThunkAction<Promise<any>, IRootState, object, AnyAction> => {
  return async (dispatch: ThunkDispatch<object, object, AnyAction>, getState: () => IRootState): Promise<any> => {
    // const userSession = await Auth.currentSession();
    // const token = userSession.getIdToken().getJwtToken();
    // A chunk of 0 days (or an unset client setting) would never advance the batch loop below.
    const chunkDays = numDaysForChunk > 0 ? numDaysForChunk : 31;
    //get the interval...
    const currentLoadedInterval = getState().crewSchedule.timeIntervalLoaded;
    try {
      //check if the data is already loaded...
      //initial time check first month
      let startOfMonth = moment(startTime).startOf("month");
      let endOfMonth = moment(startTime).endOf("month");

      let dataPromises: Promise<void>[] = [];
      const blockInterval = [];
      let dbQueryCount = 1;
      while (moment(endTime).endOf("month") >= endOfMonth) {
        const intervalLoadedCheck = checkIntervalBlockIsLoaded(currentLoadedInterval, startOfMonth);
        //if you are going to load data
        if (!intervalLoadedCheck.loaded) {
          dispatch(crewDataLoading(true));
          dispatch({
            type: Type.TIMELINE_CREW_DATA_LOADING,
            interval: intervalLoadedCheck.blockKey,
            loaded: false,
          });
          blockInterval.push(intervalLoadedCheck.blockKey);
          dispatch(
            setLoadedDataInterval({
              [intervalLoadedCheck.blockKey]: {
                startOfInterval: startOfMonth?.valueOf(),
                endOfInterval: endOfMonth?.valueOf(),
              },
            })
          );

          let lastDateForBatch = moment(startOfMonth);
          while (startOfMonth < endOfMonth) {
            lastDateForBatch.add(chunkDays, "days");
            if (lastDateForBatch > endOfMonth) {
              lastDateForBatch = endOfMonth;
            }
            //fetch the data
            dataPromises.push(fetchPairing(dispatch, startOfMonth?.valueOf(), lastDateForBatch?.valueOf()));
            dataPromises.push(fetchPlannedEvents(dispatch, startOfMonth?.valueOf(), lastDateForBatch?.valueOf()));
            dataPromises.push(fetchNFAs(dispatch, startOfMonth?.valueOf(), lastDateForBatch?.valueOf()));
            dataPromises.push(fetchDayTags(dispatch, startOfMonth?.valueOf(), lastDateForBatch?.valueOf()));
            startOfMonth.add(chunkDays, "days");

            dbQueryCount++;
            //dont have more than 4 of each query at a time on the db
            if (dbQueryCount >= 4) {
              //wait till they finish...
              await Promise.all(dataPromises);
              dataPromises = [];
              //reset count
              dbQueryCount = 0;
            }
          }
        }

        //check the next month block - reset batch start and end
        startOfMonth = endOfMonth.add(1, "month").startOf("month");
        endOfMonth = moment(startOfMonth).endOf("month");
      }

      await Promise.all(dataPromises);
      blockInterval.forEach((interval) => {
        dispatch({
          type: Type.TIMELINE_CREW_DATA_LOADING,
          interval: interval,
          loaded: true,
        });
      });
      dispatch(crewDataLoading(false));
    } catch (e) {
      console.error(e);
    }
  };
};

export const convertToPlannedPairing = async (pairingId: string, crewIds: string[]): Promise<any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const response = await API.put("skytrains", `/pairings/${pairingId}/convertToPlanned`, {
      headers: { Accept: "*/*", Authorization: token },
      body: {
        crewIds,
      },
    });
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const convertToPlannedNFA = async (nfaId: string, crewIds: string[]): Promise<any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const response = await API.put("skytrains", `/nfas/${nfaId}/convertToPlanned`, {
      headers: { Accept: "*/*", Authorization: token },
      body: {
        crewIds,
      },
    });
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const convertNfaToPairing = async ({ nfaIds, crewId }: { nfaIds: string[]; crewId: string }): Promise<any> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const response = await API.post("skytrains", "/pairings/events", {
      headers: { Accept: "*/*", Authorization: token },
      body: {
        events: {
          nonFlyingAssignmentIds: nfaIds,
        },
        crewIds: [crewId],
      },
    });
    return response;
  } catch (e) {
    console.error(e);
  }
};

export const deletePlannedEvent = async (hoveredEvent: IHoveredEvent): Promise<any> => {
  const id = hoveredEvent?.event?._id;
  const url = isIDisplayPairing(hoveredEvent.event) ? `/pairings/planned/${id}` : `/nfas/planned/${id}`;
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();

  try {
    const response = await API.del("skytrains", url, {
      headers: { Accept: "*/*", Authorization: token },
    });
    return response;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getNFA = async (nfaId: string): Promise<ICrewsNonFlyingAssignment> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const nfa = await API.get("skytrains", `/nfas/getNFA/${nfaId}`, {
      headers: { Accept: "*/*", Authorization: token },
    });
    return nfa;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const updateNFA = async (nfaId: string, update: any): Promise<INonFlyingAssignment> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const response = await API.put("skytrains", `/nfas/updateNFA/${nfaId}`, {
      headers: { Accept: "*/*", Authorization: token },
      body: update,
    });
    return response;
  } catch (e) {
    console.error(e);
    return null;
  }
};

/**
 * Get all current crew groups
 * @returns - Array of strings, each one a group label/tag
 */
export const getCrewGroups = async (): Promise<string[]> => {
  const response: string[] | IOCAPIError | null = await AmplifyAPIWrapper.Get<string[]>("/users/groups");
  // Return crew group list alphabetically.
  return Array.isArray(response) ? response.sort() : [];
};

/**
 * getCrewGroupReleaseScheduleClientSetting gets specific setting
 * @returns - IClientSettings
 */
export const getCrewGroupReleaseScheduleClientSetting = async (): Promise<IClientSettings> => {
  const settingName = "crewGroupReleaseSchedule";
  const response: IClientSettings | IOCAPIError | null = await AmplifyAPIWrapper.Get<IClientSettings>(
    `/clientSettings?settingName=${settingName}`
  );
  return response;
};

export const updateCrewGroupReleaseScheduleClientSetting = async (data: ICrewGroupReleaseSchedule): Promise<any> => {
  const response = await AmplifyAPIWrapper.Put<{ setValue: ICrewGroupReleaseSchedule }>(
    "/clientSettings/updateCrewScheduleRelease",
    { setValue: data }
  );
  return response;
};

export const getDayLogicModifiers = async (startTime: number, endTime: number): Promise<ICrewLogicModifier[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const response: ICrewLogicModifier[] = await API.get(
      "skytrains",
      `/crewModifier/list/all/${startTime}/${endTime}`,
      {
        headers: { Accept: "*/*", Authorization: token },
      }
    );
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getBusinessLogicModifiers = async (): Promise<ILogicModifier[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const response: ILogicModifier[] = await API.get("skytrains", "/modifier/list", {
      headers: { Accept: "*/*", Authorization: token },
    });
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getCrewDayLogicModifiers = async (
  crewId: string,
  start: number,
  end: number
): Promise<ICrewLogicModifier[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const response: ICrewLogicModifier[] = await API.get(
      "skytrains",
      `/crewModifier/list/crew/${crewId}/${start}/${end}`,
      {
        headers: { Accept: "*/*", Authorization: token },
      }
    );
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const deleteCrewDayLogicModifier = async (id: string): Promise<ICrewLogicModifier[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  try {
    const response: ICrewLogicModifier[] = await API.del("skytrains", `/crewModifier/${id}`, {
      headers: { Accept: "*/*", Authorization: token },
    });
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const createCrewLogicModifier = async (
  logicModifier: ILogicModifier,
  crew_id: string,
  selectedTime: number,
  note?: string
): Promise<ICrewLogicModifier[]> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  const date = new Date(selectedTime);
  date.setUTCHours(0, 0, 0, 0);
  try {
    const response: ICrewLogicModifier[] = await API.post("skytrains", "/crewModifier", {
      headers: { Accept: "*/*", Authorization: token },
      body: {
        logicModifier,
        crew_id,
        date,
        note,
      },
    });
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const setSearchedCrewmembers = (windowId: string, searchedCrewMembers: string[]): IUpdateSearchCrewMember => {
  return {
    type: Type.UPDATE_SEARCH_CREW_MEMBER,
    windowId,
    searchedCrewMembers,
  };
};

/** Function to set or toggle active open flying pairing used in the open flights tab */
export const setActiveOpenFlyingPairing = (
  windowId: string,
  requestIDs: any[],
  pairingID: string | null
): ISetActiveOpenFlyingPairing => {
  return {
    type: Type.SET_ACTIVE_OPEN_FLYING_REQUEST,
    windowId,
    requestIDs,
    pairingID,
  };
};

/** Function to store last known succesfully open flying assignment - used to refresh data */
export const setLastOpenFlyingAssignmentResponse = (
  assignedAt: Date,
  requestResponse: any
): ISetLastOpenFlyingAssignmentResponse => {
  return {
    type: Type.SET_LAST_OPEN_FLYING_ASSIGNMENT_RESPONSE,
    assignedAt,
    requestResponse,
  };
};

/**
 * setHideDropOffHours - redux action to set whether or not to hide normal status drop off hours
 * @param hideDropOffHours - whether or not to hide normal status drop off hours
 */
export const setHideDropOffHours = (hideDropOffHours: boolean): ISetHideDropOffHours => {
  return { type: Type.SET_HIDE_DROPOFF_HOURS, hideDropOffHours };
};

export const setHideTimeBetweenEvents = (
  windowId: string,
  hideTimeBetweenEvents: boolean
): ISetWindowHideTimeBetweenEvents => {
  return { type: Type.SET_HIDE_TIME_BETWEEN_EVENTS, windowId, hideTimeBetweenEvents };
};

export const setHideCheckInCheckOutTimes = (
  windowId: string,
  hideCheckInCheckOutTimes: boolean
): ISetWindowHideCheckInCheckOutTimes => {
  return { type: Type.SET_HIDE_CHECKIN_CHECKOUT_TIMES, windowId, hideCheckInCheckOutTimes };
};

export const addPairingEditor = (pairing: IPairing): IAddPairingEditor => {
  return {
    type: Type.ADD_PAIRING_EDITOR,
    pairing,
  };
};

export const removePairingEditor = (pairingId: string): IRemovePairingEditor => {
  return {
    type: Type.REMOVE_PAIRING_EDITOR,
    pairingId,
  };
};

export const updatePairingEditor = (pairingId: string, updates: Partial<IPairing>): IUpdatePairingEditor => {
  return {
    type: Type.UPDATE_PAIRING_EDITOR,
    pairingId,
    updates,
  };
};

export const removeWindowProperties = (windowId: string): IWindowProperties => {
  return {
    type: Type.REMOVE_WINDOW_PROPERTIES,
    windowId,
  };
};
